import React, { Component } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { fetchUserPackages, packageCheckoutAbandon, packageCheckoutInit, packagePaymentFormSubmit } from '../../../actions/creators/packages';
import { getPackages, isPackagesLoading, getPackagesError, getPackagePurchaseError, getPackagePurchaseSuccess, isPackagePurchaseProcessing, getCinemas } from '../../../reducers';
import LogoLoading from '../../LogoLoading';
import { breakpoints } from '../../../utils/styles';
import Button from '../../Button';
import PaymentSidebar from '../../checkout/PaymentSidebar';
import { CHECKOUT_CONTEXT_LOYALTY_PACKAGES } from '../../../actions/creators/checkout';
import LoyaltyPurchaseOption from './LoyaltyPurchaseOption';
import PurchaseSuccessMessage from './PurchaseSuccessMessage';
import CardAnimation from '../../checkout/CardAnimation';

const CinemaSelectBlock = styled.div`
  height: auto;
  opacity: 1;
  transition: opacity 0.5s ease-in;
  margin-bottom: 2.3125rem;

  &.hidden {
    height: 0;
    opacity: 0;
    transition: opacity 0s ease-in;
  }
`;

const PackagesGrid = styled.div`
  margin-top: 2rem;

  @media screen and (min-width: ${breakpoints.desktop}px) {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: 2rem;
  }
`;

const ErrorContainer = styled.div`
  background: #fdf2f2;
  border-radius: 8px;
  color: #e74c3c;
  margin: 2rem 0;
  padding: 3rem;
  text-align: center;
`;

const EmptyContainer = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  color: #666;
  margin: 2rem 0;
  padding: 3rem;
  text-align: center;
`;

interface Props {
  error: string | null;
  isLoading: boolean;
  isPurchasing: boolean;
  cinemas: any[];
  packages: any[];
  paymentGateway: string;
  purchaseError: string | null;
  successData: any;
  fetchUserPackages: (data: any) => void;
  onLoad: () => void;
  packageCheckoutAbandon: () => void;
  packageCheckoutInit: (data: any) => void;
  packagePaymentFormSubmit: (data: any) => void;
}

interface State {
  selectedItems: any[] | null;
  selectedCinemaId: number;
}

class PackagesPage extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.handleBackToPackages = this.handleBackToPackages.bind(this);

    this.state = {
      selectedItems: null,
      selectedCinemaId: 0,
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    this.props.onLoad();
  }

  handleCinemaChange = (e) => {
    const cinemaId = parseInt(e.target.value);
    this.setState({ selectedCinemaId: cinemaId });

    if (!!cinemaId) {
      this.props.fetchUserPackages({ cinemaId });
    }
  };

  handlePurchasePackage = (packageData) => {
    const items = [{
      id: packageData.id,
      name: packageData.name,
      description: packageData.description,
      quantity: 1,
      price: packageData.price
    }];

    this.setState({
      selectedItems: items
    })
  };

  handleBackToPackages = () => {
    this.setState({
      selectedItems: null
    });
  };

  handlePaymentSubmit = (paymentData) => {
    this.props.packagePaymentFormSubmit(paymentData);
  };

  handlePaymentSuccess = () => {
    this.handleBackToPackages();
  };

  handleRetryPayment = () => {
    this.props.packageCheckoutAbandon();
  };

  render() {
    const { cinemas, packages, isLoading, error } = this.props;

    if (isLoading) {
      return <LogoLoading />;
    }

    if (this.props.successData) {
      return this.renderSuccessScreen();
    }

    return (
      <div>
        <p>
          Por favor <strong>selecciona tu cine preferido</strong> para ver las
          opciones de paquetes:
        </p>

        <CinemaSelectBlock className="col-md-6 pl-0">
          <label htmlFor="preferencesCine">Cine preferido</label>
          <select
            className="form-control"
            id="preferencesCine"
            onChange={this.handleCinemaChange}
            value={this.state.selectedCinemaId}
          >
            <option key="0" value="0">
              Selecciona tu cine
            </option>
            {cinemas.map((cinema) => (
              <option value={cinema.id} key={cinema.id}>
                {cinema.name}
              </option>
            ))}
          </select>
        </CinemaSelectBlock>

        <p className="mb-4">
          Recuerda que los beneficios incluidos en el paquete serán <strong>únicamente válidos en el cine seleccionado.</strong>
        </p>

        {!!this.state.selectedCinemaId && (
          <React.Fragment>
            <p>
              Elige la opción de paquete más te guste y disfruta los beneficios
              de ser Cinemex Loop.
            </p>

            {error && (
              <ErrorContainer>
                <h3>Error al cargar los paquetes</h3>
                <p>Por favor, intenta nuevamente más tarde.</p>
              </ErrorContainer>
            )}

            {!packages.length && !error && (
              <EmptyContainer>
                <h3>No tienes paquetes disponibles</h3>
                <p>
                  En este momento no hay paquetes disponibles para tu cuenta.
                </p>
              </EmptyContainer>
            )}

            <PackagesGrid>
              {packages.map((pkg) => (
                <LoyaltyPurchaseOption
                  key={pkg.id}
                  option={pkg}
                  onSelect={this.handlePurchasePackage}
                  selected={
                    this.state.selectedItems && this.state.selectedItems.length
                      ? this.state.selectedItems[0].id === pkg.id
                      : false
                  }
                />
              ))}
            </PackagesGrid>

            {this.renderPaymentSidebar()}
            {this.props.isPurchasing && <CardAnimation />}
          </React.Fragment>
        )}
      </div>
    );
  }

  renderPaymentSidebar() {
    if (!this.state.selectedItems || !this.state.selectedItems.length) {
      return null;
    }

    return (
      <PaymentSidebar
        context={CHECKOUT_CONTEXT_LOYALTY_PACKAGES}
        cash={this.state.selectedItems[0].price}
        ccRequired
        paymentGateway={this.props.paymentGateway}
        method="credit-card"
        transactionData={this.getTransactionData()}
        onCancel={this.handleBackToPackages}
      />
    );
  }

  renderSuccessScreen() {
    let subtitle = this.state.selectedItems && this.state.selectedItems.length ? `Has comprado el paquete ${this.state.selectedItems[0].name.toUpperCase()} 🙌` : undefined;

    return <PurchaseSuccessMessage
      title="¡FELICIDADES!"
      subtitle={subtitle}
      content={
        <>
          <p>Los beneficios ya han sido asignados en tu tarjeta Cinemex Loop.</p>
          <p>Puedes utilizarlos en taquilla, dulcería y nuestros medios digitales. ¡Que los disfrutes!</p>
        </>
      }
    />;
  }

  getTransactionData() {
    const data = {
      items: this.state.selectedItems,
      cinema_id: this.state.selectedCinemaId
    };

    return {
      context: CHECKOUT_CONTEXT_LOYALTY_PACKAGES,
      data
    };
  }
}

const mapStateToProps = state => ({
  cinemas: getCinemas(state),
  packages: getPackages(state),
  isLoading: isPackagesLoading(state),
  isPurchasing: isPackagePurchaseProcessing(state),
  error: getPackagesError(state),
  paymentGateway: 'stripe-elements', // @todo make this dynamic
  purchaseError: getPackagePurchaseError(state),
  successData: getPackagePurchaseSuccess(state),
});

const mapDispatchToProps = dispatch => ({
  onLoad: () => dispatch(packageCheckoutInit()),
  fetchUserPackages: (data) => dispatch(fetchUserPackages(data)),
  packageCheckoutAbandon: () => dispatch(packageCheckoutAbandon()),
  packageCheckoutInit: (data) => dispatch(packageCheckoutInit(data)),
  packagePaymentFormSubmit: (data) => dispatch(packagePaymentFormSubmit(data)),
});

const PackagesPageConnected = connect(mapStateToProps, mapDispatchToProps)(PackagesPage);

export default PackagesPageConnected;